import { CampaignMessage } from 'sections/onsite-campaigns/pages/CampaignEditor/contexts/CampaignEditorState';
import { DisplayWebContentAction, DisplayWebContentState } from './DisplayWebContentState';
import { TriggerType, WebsiteContentMessage } from 'sections/onsite-campaigns/types';

const assign = (state: DisplayWebContentState, id: string, property: Partial<WebsiteContentMessage>) => {
  const message: CampaignMessage<WebsiteContentMessage> = {
    ...(state[id] ?? ({} as any)),
    displayType: 'Overlay',
    messageType: 'onsite',
    ...property,
  };

  return { ...state, [id]: message };
};

export const DisplayWebContentReducer = (state: DisplayWebContentState, action: DisplayWebContentAction) => {
  switch (action.type) {
    case 'ADD_DISPLAY_WEB_CONTENT': {
      const { id, segmentId } = action.payload;

      const messageCount =
        Object.keys(state)
          .map(k => state[k])
          .filter(s => s.messageType === 'onsite').length + 1;
      const defaultName = `Web content #${messageCount}`;

      return assign(state, id, {
        id,
        segmentId,
        name: defaultName,
        triggers: {
          [TriggerType.ExitIntent]: {
            active: false,
            value: [],
          },
          [TriggerType.Inactivity]: {
            active: false,
            value: 0,
          },
        },
      });
    }
    case 'UPDATE_DISPLAY_WEB_CONTENT_NAME': {
      const { id, name } = action.payload;

      return assign(state, id, {
        name: name,
      });
    }
    case 'SET_TEMPLATE': {
      const { id, templateId, templateName, templateType, supportedLanguage, isEmbedded } = action.payload;

      return assign(state, id, {
        template: {
          id: templateId,
          name: templateName,
          type: templateType,
          supportedLanguage,
          isEmbedded,
        },
        embeddedPosition: isEmbedded ? state[id].embeddedPosition : undefined,
        embeddedSelector: isEmbedded ? state[id].embeddedSelector : undefined,
      });
    }
    case 'REMOVE_DISPLAY_WEBSITE_CONTENT': {
      const newState = { ...state };
      delete newState[action.payload.id];

      return newState;
    }
    case 'CLEAR_DISPLAY_SEGMENTATION': {
      const { id } = action.payload;

      return assign(state, id, {
        segmentationRules: [],
      });
    }
    case 'DISPLAY_SEGMENTATION_CHOSEN': {
      const { id } = action.payload;

      return assign(state, id, {
        segmentationRules: undefined,
      });
    }
    case 'SAVE_DISPLAY_SEGMENTATION': {
      const { id, segmentation } = action.payload;

      return assign(state, id, {
        segmentationRules: segmentation,
      });
    }
    case 'SET_CONTROL_GROUP': {
      const { id, controlGroupEnabled, controlGroupWeight } = action.payload;

      return assign(state, id, {
        controlGroupEnabled: controlGroupEnabled,
        controlGroupWeight: controlGroupWeight,
      });
    }
    case 'SET_EMBEDDED_SELECTOR': {
      const { id, selector } = action.payload;

      return assign(state, id, { embeddedSelector: selector });
    }
    case 'SET_EMBEDDED_POSITION': {
      const { id, position } = action.payload;

      return assign(state, id, { embeddedPosition: position });
    }
    case 'UPDATE_MESSAGE_TRIGGERS': {
      const { id, triggers } = action.payload;

      return assign(state, id, { triggers });
    }
    case 'SET_SEGMENT_START_DATE': {
      const defaultTime = '00:00';
      const messageId = action.payload.id;
      let startTime = state[messageId]?.scheduling?.startTime ?? defaultTime;
      if (!state[messageId]?.scheduling?.startTime) {
        startTime = defaultTime;
      }
      return assign(state, action.payload.id, {
        scheduling: {
          ...state[action.payload.id]?.scheduling,
          startDate: action.payload.startDate,
          startTime,
        },
      });
    }
    case 'SET_SEGMENT_START_TIME': {
      return assign(state, action.payload.id, {
        scheduling: {
          ...state[action.payload.id]?.scheduling,
          startTime: action.payload.startTime,
        },
      });
    }
    case 'SET_SEGMENT_END_DATE': {
      const defaultTime = '23:59';
      const messageId = action.payload.id;
      let endTime = state[messageId]?.scheduling?.endTime ?? defaultTime;
      if (!action.payload.endDate) {
        endTime = defaultTime;
      }
      return assign(state, action.payload.id, {
        scheduling: {
          ...state[action.payload.id]?.scheduling,
          endDate: action.payload.endDate,
          endTime,
        },
      });
    }
    case 'SET_SEGMENT_END_TIME': {
      return assign(state, action.payload.id, {
        scheduling: {
          ...state[action.payload.id]?.scheduling,
          endTime: action.payload.endTime,
        },
      });
    }
    case 'REMOVE_SEGMENT_DATETIMES': {
      return assign(state, action.payload.id, {
        scheduling: {},
      });
    }
  }
};
